import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  storageGetAuth,
  storageIsNewAuth,
  storageSetAuth,
  storageSetCards,
} from './utils/storage';
import useEnhancedAuth, { useCookieAuth } from './EnhancedAuth';
import { LoadingController } from './utils/LoadingController';
import { stopRefreshTokenTimeout } from './services/apiHandler';
import { getAppSuccess } from './utils/messages';
import { useTranslation } from 'react-i18next';
import { useToaster } from './hooks/useToaster';

type PostMessageContextType = {
  backCommands: number;
  value?: any;
};

const PostMessageContext = createContext<PostMessageContextType | undefined>(
  undefined,
);

type PostMessageReceiverProps = {
  children?: React.ReactNode;
};

export const PostMessageReceiver: React.FC<PostMessageReceiverProps> = ({
  children,
}) => {
  const auth = useEnhancedAuth();
  const [present] = useToaster();
  const [t] = useTranslation('global');

  const [backCommands, setBackCommands] = useState<number>(0);

  const handleMessage = (event: MessageEvent) => {
    if (typeof event.data === 'string') {
      if (event.data.includes('auth=')) {
        if (useCookieAuth()) {
          const startIndex = event.data.indexOf('auth=') + 'auth='.length;
          const dataParts = event.data.substring(startIndex).split(';');
          if (dataParts.length >= 1) {
            const token = {
              access_token: dataParts[0],
              refresh_token: dataParts?.[1],
            };
            if (storageIsNewAuth(token) || !storageGetAuth()?.length) {
              stopRefreshTokenTimeout();

              storageSetAuth(token);
              storageSetCards(null);
              auth.login();

              if (LoadingController.isForcedLoading()) {
                LoadingController.forceHide();
                present(getAppSuccess(t('home.tokenrefreshed')));
              }
            }
          }
        }
      } else if (event.data == 'ionicGoBack') {
        setBackCommands(new Date().getTime());
      }
    }
  };

  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  const value = useMemo(
    () => ({
      backCommands,
    }),
    [backCommands],
  );

  return (
    <PostMessageContext.Provider value={value}>
      {children}
    </PostMessageContext.Provider>
  );
};

export const usePostMessages = () => {
  const context = useContext(PostMessageContext);
  if (context === undefined) {
    throw new Error(
      'usePostMessages must be used within a PostMessageReceiver',
    );
  }
  return context;
};
